import { useCallback, useRef, useState } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? "";
const MAX_RETRIES = 4;

/**
 * Hook for managing a Cloudflare Turnstile widget.
 *
 * Returns:
 * - `token`     – current Turnstile token (null until verified)
 * - `hasError`  – true when all retries are exhausted
 * - `resetToken`– call to force a new challenge (also clears error state)
 * - `siteKey`   – public site key (needed by the widget)
 * - `widgetRef` – ref to pass to the Turnstile component
 * - `onSuccess` – callback for token generation
 * - `onExpire`  – callback for token expiration
 * - `onError`   – callback for widget errors (auto-retries with backoff)
 */
export function useTurnstile() {
  const [token, setToken] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const widgetRef = useRef<{ reset: () => void } | undefined>(undefined);
  const retryCount = useRef(0);

  const resetToken = useCallback(() => {
    setToken(null);
    setHasError(false);
    retryCount.current = 0;
    widgetRef.current?.reset();
  }, []);

  const onSuccess = useCallback((t: string) => {
    retryCount.current = 0;
    setHasError(false);
    setToken(t);
  }, []);

  const onExpire = useCallback(() => {
    setToken(null);
    widgetRef.current?.reset();
  }, []);

  const onError = useCallback(() => {
    if (retryCount.current < MAX_RETRIES) {
      retryCount.current += 1;
      const delay = Math.min(1000 * Math.pow(2, retryCount.current - 1), 8000);
      console.warn(
        `[Turnstile] Error – retrying (${retryCount.current}/${MAX_RETRIES}) in ${delay}ms`,
      );
      setTimeout(() => {
        widgetRef.current?.reset();
      }, delay);
    } else {
      console.error("[Turnstile] Widget failed after retries");
      setHasError(true);
    }
  }, []);

  return {
    token,
    hasError,
    resetToken,
    siteKey: SITE_KEY,
    widgetRef,
    onSuccess,
    onExpire,
    onError,
  };
}
